import {Breakpoints, styled} from '@volkswagen-onehub/components-core'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {boxedContainer, gridUtils} from '../../../assets/css-vars/gridUtils'
import {
    setFilteredModels,
    setSelectedCarNames,
} from '../../../features/general/generalSlice'
import TrackingAction from '../../../tracking-manager'
import SingleModelInterface from '../../../types/singleModelInterface'
import {useFeatureServices} from '../../../use-feature-services'
import Brands from '../../../utils/brand/brand'
import CustomerTypes from '../../../utils/customerTypes/customerTypes'
import filterByCarModel from '../../../utils/filters/filterByCarModel'
import filterByCarNames from '../../../utils/filters/filterByCarNames'
import filterByCarType from '../../../utils/filters/filterByCarType'
import filterByCustomerType from '../../../utils/filters/filterByCustomerType'
import filterByEngine from '../../../utils/filters/filterByEngine'
import getCarEnginesOptions from '../../../utils/filters/getCarEnginesOptions'
import getCarModelOptions from '../../../utils/filters/getCarModelOptions'
import getCarTypesOptions from '../../../utils/filters/getCarTypesOptions'
import {getCarsInCategoriesOptions} from '../../../utils/filters/getCarsInCategoriesOptions'
import getNewOptionValue from '../../../utils/filters/getNewOptionValue'
import setNewCollectionValues from '../../../utils/filters/setNewCollectionValues'
import {
    getFilterSearchResultPageloadData,
    getVWBasicFilterSelectClickData,
    getVehicleFilterSearchResultLoadData,
} from '../../../utils/tracking/getTrackingData'
import FilterList from '../../../utils/tracking/tracking-utils/interfaces/FilterList'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import {RootState} from '../../rootReducer'
import {AppDispatch} from '../../thunk'
import VwMultiSelect from '../form/VwMultiSelect'
import FilterByCarCategories from './filterFields/filterByCarCategories'
import FilterByCarEngine from './filterFields/filterByCarEngine'
import FilterByCarModel from './filterFields/filterByCarModel'
import ResetFilters from './resetFilters'
import filterByDealerCode from '../../../utils/filters/FilterByDealer'

// scss for container
const FilterWrapper = styled.div`
    ${boxedContainer}

    padding-top: 50px;

    @media (min-width: ${Breakpoints.b560}px) {
        padding-top: 0;
    }
`

const FilterInner = styled.div`
    max-width: 780px;
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};

    @media (min-width: ${Breakpoints.b1280}px) {
        padding-right: 0;
        padding-left: 0;
    }
`

const FiltersGrid = styled.div`
    display: grid;
    align-items: center;
    gap: 25px;

    @media (min-width: ${Breakpoints.b560}px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        gap: 40px;
    }
`

export function FiltersContainer(): React.JSX.Element {
    const {t} = useTranslation('app')
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch
    const models = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )
    const filteredModels: SingleModelInterface[] = useSelector(
        (state: RootState) => state.general.filteredModels
    )
    const selectedCarTypes = useSelector(
        (state: RootState) => state.general.selectedCarTypes
    )
    const selectedEngineTypes = useSelector(
        (state: RootState) => state.general.selectedEngineTypes
    )
    const selectedCarModelTypes = useSelector(
        (state: RootState) => state.general.selectedCarModelTypes
    )
    const selectedCarNames = useSelector(
        (state: RootState) => state.general.selectedCarNames
    )
    const brand = useSelector((state: RootState) => state.general.brand)
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const {tracking: trackingManager} = useFeatureServices()
    const numberOfModelsWithActivePromotions = useSelector(
        (state: RootState) => state.general.numberOfModelsWithActivePromotions
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const customerType = useSelector(
        (state: RootState) => state.general.customerType
    )
    const carsByCustomerTypeNumber = useSelector(
        (state: RootState) => state.general.carsByCustomerTypeNumber
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const [userHasInteracted, setUserHasInteracted] = useState(false)

    function translateEngines(engines: string[]): string[] {
        return engines.map((engine: string) => t(`fuels.${engine}`))
    }

    useEffect(() => {
        if (
            numberOfModelsWithActivePromotions &&
            checkTrackingManager(trackingManager)
        ) {
            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSearchResult_Pageload,
                getFilterSearchResultPageloadData(
                    numberOfModelsWithActivePromotions,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }, [numberOfModelsWithActivePromotions])

    useEffect(() => {
        const translatedEngines = translateEngines(selectedEngineTypes)

        if (translatedEngines.length && checkTrackingManager(trackingManager)) {
            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSelect_Click,
                getVWBasicFilterSelectClickData(
                    'Alimentazione',
                    'Alimentazione',
                    translatedEngines,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }, [selectedEngineTypes])

    useEffect(() => {
        if (selectedCarTypes.length && checkTrackingManager(trackingManager)) {
            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSelect_Click,
                getVWBasicFilterSelectClickData(
                    'Categoria',
                    'Categoria',
                    selectedCarTypes,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }, [selectedCarTypes])

    useEffect(() => {
        if (
            selectedCarModelTypes.length &&
            checkTrackingManager(trackingManager)
        ) {
            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSelect_Click,
                getVWBasicFilterSelectClickData(
                    'Modello',
                    'Modello',
                    selectedCarModelTypes,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }, [selectedCarModelTypes])

    useEffect(() => {
        if (brand === Brands.VW) {
            setFilterModelsByTypeAndEngineAndModelAndDealer(
                selectedCarTypes,
                selectedEngineTypes,
                selectedCarModelTypes,
                models
            )
        } else {
            setFilterModelsByCarNamesAndCustomerTypeAndDealer(
                selectedCarNames,
                customerType,
                models
            )
        }
    }, [
        selectedCarTypes,
        selectedEngineTypes,
        selectedCarNames,
        selectedCarModelTypes,
        customerType,
        models,
        dealerCode,
    ])

    function setFilterModelsByCarNamesAndCustomerTypeAndDealer(
        carNames: string[],
        selectedCustomerType: CustomerTypes,
        allModels: SingleModelInterface[]
    ): void {
        const carsFilteredByNames = filterByCarNames(carNames, allModels)
        const carsFilteredByCustomerType = filterByCustomerType(
            selectedCustomerType,
            carsFilteredByNames
        )

        const carsFilteredByCustomerTypeAndDealer = filterByDealerCode(
            dealerCode,
            carsFilteredByCustomerType
        )

        dispatch(setFilteredModels(carsFilteredByCustomerTypeAndDealer))

        if (checkTrackingManager(trackingManager) && userHasInteracted) {
            const filterList: FilterList[] = [
                {
                    type: 'multi_value',
                    name: 'CG',
                    values: filteredModels.length
                        ? filteredModels.map((car) => car.name)
                        : [''],
                },
            ]

            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSearchResult_Load,
                getVehicleFilterSearchResultLoadData(
                    filteredModels.length,
                    filterList,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }

    function setFilterModelsByTypeAndEngineAndModelAndDealer(
        carTypes: string[],
        engineTypes: string[],
        carModelTypes: string[],
        allModels: SingleModelInterface[]
    ): void {
        const carsFilteredByType = filterByCarType(carTypes, allModels)

        const carsFilteredByModel = filterByCarModel(
            carModelTypes,
            carsFilteredByType
        )

        // get models by engine type; if no engine was set from filters, get models filtered by type
        const carsFiltered = filterByEngine(engineTypes, carsFilteredByModel)

        const carsFilteredByDealer = filterByDealerCode(
            dealerCode,
            carsFiltered
        )

        dispatch(setFilteredModels(carsFilteredByDealer))

        if (checkTrackingManager(trackingManager) && userHasInteracted) {
            const filterList: FilterList[] = [
                {
                    type: 'multi_value',
                    name: 'CA',
                    values: carTypes.length ? carTypes : [''],
                },
                {
                    type: 'multi_value',
                    name: 'FT',
                    values: engineTypes.length
                        ? translateEngines(engineTypes)
                        : [''],
                },
                {
                    type: 'multi_value',
                    name: 'CG',
                    values: carModelTypes.length ? carModelTypes : [''],
                },
            ]

            setTracking(
                trackingManager,
                TrackingAction.VWBasic_FilterSearchResult_Load,
                getVehicleFilterSearchResultLoadData(
                    carsFiltered.length,
                    filterList,
                    trackingBrand,
                    isShowroom
                )
            )
        }
    }

    {
        /*
         * Element must always be in DOM to set initial filters
         * Set to hide if there are no cars filtered by customer type
         * In brands without distinction between customer types, this number
         * will always be set to the number of cars with active promotions
         * */
    }

    const modelFilteredByDealer =
        dealerCode !== ''
            ? models.filter(
                  (model: SingleModelInterface) => model.in_specialization
              )
            : models

    return (
        <FilterWrapper
            style={{display: carsByCustomerTypeNumber ? '' : 'none'}}
        >
            <FilterInner>
                {brand === Brands.VW ? (
                    <FiltersGrid>
                        <FilterByCarCategories
                            selectedCarTypes={selectedCarTypes}
                            carTypeOptions={getCarTypesOptions(
                                modelFilteredByDealer
                            )}
                            onClick={() => setUserHasInteracted(true)}
                        />

                        <FilterByCarEngine
                            selectedEngines={selectedEngineTypes}
                            engineOptions={getCarEnginesOptions(
                                modelFilteredByDealer
                            )}
                            onClick={() => setUserHasInteracted(true)}
                        />

                        <FilterByCarModel
                            selectedCarModel={selectedCarModelTypes}
                            carModelOptions={getCarModelOptions(
                                modelFilteredByDealer
                            )}
                            onClick={() => setUserHasInteracted(true)}
                        />
                    </FiltersGrid>
                ) : (
                    <FiltersGrid>
                        {getCarsInCategoriesOptions(
                            modelFilteredByDealer,
                            customerType
                        ) ? (
                            <VwMultiSelect
                                placeholderName={t('filters.carName')}
                                options={getCarsInCategoriesOptions(
                                    modelFilteredByDealer,
                                    customerType
                                )}
                                onChangeMain={(values) => {
                                    setUserHasInteracted(true)
                                    const newValues = setNewCollectionValues(
                                        selectedCarNames,
                                        values
                                    )

                                    dispatch(setSelectedCarNames(newValues))
                                }}
                                onChangeChildren={(values) => {
                                    setUserHasInteracted(true)
                                    const newValues = getNewOptionValue(
                                        selectedCarNames,
                                        values
                                    )

                                    dispatch(setSelectedCarNames(newValues))
                                }}
                                selectedOptionsFromState={selectedCarNames}
                            />
                        ) : undefined}
                    </FiltersGrid>
                )}

                {selectedCarTypes.length ||
                selectedEngineTypes.length ||
                selectedCarModelTypes.length ||
                selectedCarNames.length ? (
                    <ResetFilters
                        filteredCars={filteredModels}
                        isShowroomMode={isShowroom}
                        trackingBrandLabel={trackingBrand}
                        carTypes={selectedCarTypes}
                        engineTypes={selectedEngineTypes}
                        translatedEngines={translateEngines(
                            selectedEngineTypes
                        )}
                        modelFilteredByDealer={modelFilteredByDealer}
                    />
                ) : undefined}
            </FilterInner>
        </FilterWrapper>
    )
}
